import React, { useEffect, useState, createContext } from "react";
import { useNavigate } from "react-router";
import instance from "services/api";
import tokenService from "services/token.service";
import { permissionsService } from "services/permissions.service";
import { Navigate } from "react-router";
import Spinner from "components/functional/spinner";

export const MembershipContext = createContext(null);

const membershipData = (WrappedComponent) => {
  return (props) => {
    const [isProfileComplete, setIsProfileComplete] = useState(null);
    const [membership, setMembership] = useState(null);
    const [teams, setTeams] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const userId = tokenService.getUserId();

    const updateMembership = (newMembership) => {
      setMembership(newMembership);
    };

    const loadUser = async () => {
      try {
        const response = await instance.get("/auth/profile-completeness");
        let { profileComplete, role } = response.data;
        if (profileComplete) {
          setIsProfileComplete(true);
        } else {
          if (role === "admin") {
            navigate("/complete-admin-profile"); // Redirect to profile completion route
          }
        }
      } catch (error) {
        console.error("Error checking profile completeness:", error);
      }
    };

    const fetchMembershipData = async () => {
      try {
        const response = await instance.get(`/users/${userId}/load`);
        setMembership(response.data);
      } catch (error) {
        console.error("Error fetching membership data:", error);
      }
    };

    const loadTeams = async () => {
      try {
        const response = await instance.get(`/teams/${userId}/teams`);
        setTeams(response.data);
      } catch (error) {
        console.error("Error fetching membership data:", error);
      }
    };

    const loadPermissions = async () => {
      try {
        const response = await instance.get(`/users/permissions`);
        permissionsService.setPermissions(response.data);
      } catch (error) {
        console.error("Error fetching permissions:", error);
      }
    };

    useEffect(() => {
      const initialize = async () => {
        if (!isProfileComplete) await loadUser();
        await Promise.all([
          fetchMembershipData(),
          loadTeams(),
          loadPermissions(),
        ]);
        setIsLoading(false);
      };

      initialize();
    }, []);

    if (isLoading) {
      return <Spinner />;
    }

    // If the user is an admin and has only one team and no name, redirect to the first step
    if (teams && teams.length === 1 && !teams[0].name) {
      return (
        <Navigate
          to="/first-step"
          state={{ role: "admin", team_id: teams[0].team_id }}
        />
      );
    }

    return (
      <MembershipContext.Provider
        value={{ membership, teams, updateMembership }}
      >
        <WrappedComponent {...props} />
      </MembershipContext.Provider>
    );
  };
};

export default membershipData;
